// SettingsSheet.js — the gear menu (Synapse parity): response style,
// clinic/conversation mode, microphone selection. Bottom-anchored above
// the chat bar, same layer slot as the conversation overlay.
import React, { useEffect, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { T } from './liveTheme';

const Segmented = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    {options.map((opt) => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        style={{
          flex: 1,
          padding: '7px 0',
          fontSize: 11,
          fontWeight: 600,
          borderRadius: 6,
          cursor: 'pointer',
          background:
            value === opt.value ? 'rgba(107,216,136,0.16)' : T.surfaceMuted,
          color: value === opt.value ? T.greenBright : T.textSoft,
          border: `1px solid ${
            value === opt.value ? 'rgba(107,216,136,0.55)' : T.border
          }`,
        }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const Row = ({ label, hint, children }) => (
  <div style={{ marginBottom: 14 }}>
    <p
      style={{
        margin: '0 0 6px',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.5px',
        color: T.textFaint,
      }}
    >
      {label}
    </p>
    {children}
    {hint && (
      <p style={{ margin: '5px 0 0', fontSize: 10, color: T.textSecondary }}>
        {hint}
      </p>
    )}
  </div>
);

const SettingsSheet = () => {
  const { state, actions } = useComprehendLive();
  const [mics, setMics] = useState([]);

  useEffect(() => {
    navigator.mediaDevices
      ?.enumerateDevices()
      .then((devices) =>
        setMics(devices.filter((d) => d.kind === 'audioinput')),
      )
      .catch(() => setMics([]));
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 7,
        background: T.surface,
        borderTop: `1px solid ${T.border}`,
        borderRadius: '10px 10px 0 0',
        padding: '8px 12px 10px',
        animation: 'cplRise .18s ease-out',
        boxShadow: '0 -8px 24px rgba(13,35,30,0.45)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 10,
        }}
      >
        <i
          className="fa fa-cog"
          aria-hidden="true"
          style={{ color: T.textSecondary, fontSize: 12 }}
        />
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.5px',
            color: T.textFaint,
          }}
        >
          AGENT SETTINGS
        </span>
        <i
          className="fa fa-times"
          aria-hidden="true"
          onClick={actions.toggleSettings}
          style={{
            marginLeft: 'auto',
            color: T.textSecondary,
            fontSize: 12,
            cursor: 'pointer',
            padding: 4,
          }}
        />
      </div>

      <Row
        label="PASSIVE LISTENING"
        hint={
          state.autoListen
            ? 'Wake word and the ambient brain are live, and start with the page next time.'
            : 'Nothing opens the mic. Turn on to enable "Hey Comprehend" and ambient capture — the choice is remembered.'
        }
      >
        <Segmented
          options={[
            { value: false, label: '🚫 Off' },
            { value: true, label: '👂 Listening' },
          ]}
          value={state.autoListen}
          onChange={actions.setAutoListen}
        />
      </Row>

      <Row
        label="RESPONSE STYLE"
        hint={
          {
            brief: 'One short sentence, no preamble.',
            normal: 'Concise clinical answers.',
            wordy: 'Detailed responses with reasoning when helpful.',
          }[state.agentVerbosity]
        }
      >
        <Segmented
          options={[
            { value: 'brief', label: '⚡ Brief' },
            { value: 'normal', label: '— Normal' },
            { value: 'wordy', label: '📚 Wordy' },
          ]}
          value={state.agentVerbosity}
          onChange={actions.setVerbosity}
        />
      </Row>

      <Row
        label="MODE"
        hint={
          state.agentMode === 'clinic'
            ? 'Mic closes after each answer — say "Hey Comprehend" per command.'
            : 'Natural back-and-forth: the mic stays open while the chat is up.'
        }
      >
        <Segmented
          options={[
            { value: 'clinic', label: '🏥 Clinic' },
            { value: 'conversation', label: '💬 Conversation' },
          ]}
          value={state.agentMode}
          onChange={actions.setAgentMode}
        />
      </Row>

      <Row label="MICROPHONE">
        <select
          value={state.micId}
          onChange={(e) => actions.setMicId(e.target.value)}
          style={{
            width: '100%',
            background: T.surfaceMuted,
            color: T.textSoft,
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            padding: '7px 8px',
            fontSize: 11,
          }}
        >
          <option value="">Default microphone</option>
          {mics.map((m) => (
            <option key={m.deviceId} value={m.deviceId}>
              {m.label || `Microphone (${m.deviceId.slice(0, 6)}…)`}
            </option>
          ))}
        </select>
      </Row>
    </div>
  );
};

export default SettingsSheet;
